import { useState } from 'react';
import { Download } from 'lucide-react';
import { Button } from '@/shared/components/ui/Button';
import { useErrorToast } from '@/shared/hooks/useErrorToast';
import { maintenanceApi } from '@/api/maintenanceApi';

interface MaintenanceExportButtonProps {
  farmId: string;
  pondId: string | null;
  pondName?: string;
  disabled?: boolean;
}

export function MaintenanceExportButton({
  farmId,
  pondId,
  pondName,
  disabled = false,
}: MaintenanceExportButtonProps) {
  const [isExporting, setIsExporting] = useState(false);
  const { showError, showAxiosError } = useErrorToast();

  const handleExport = async () => {
    if (!pondId) {
      showError(400, 'Please select a pond to export');
      return;
    }

    try {
      setIsExporting(true);

      const blob = await maintenanceApi.exportChecks(farmId, {
        pondId: pondId || undefined,
      });

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;

      const filename = `maintenance_export_${pondName || 'pond'}_${new Date().toISOString().split('T')[0]}.xlsx`;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);

      console.log('Export completed successfully:', filename);
    } catch (error) {
      console.error('Export error:', error);
      showAxiosError(error);
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <Button
      variant="outline"
      size="md"
      onClick={handleExport}
      disabled={disabled || !pondId || isExporting}
      className="gap-2"
    >
      <Download className={`h-4 w-4 ${isExporting ? 'animate-bounce' : ''}`} />
      {isExporting ? 'Exporting...' : 'Export to Excel'}
    </Button>
  );
}
